"use client";

import GetDeliveryLocationDetails from "@/app/components/GetDeliveryLocationDetails";
import useSearchRouter from "@/app/hooks/useSearchRouter";
import TopLevelCategoryFacet from "@/app/search/facets/categories/topLevelCategoryFacet";
import CustomFacet from "@/app/search/facets/customFacet";
import { STAR_RATING_ATTRIBUTE_NAME } from "@/app/search/facets/facetBar";
import GenericFacet from "@/app/search/facets/genericFacet";
import RatingFacet from "@/app/search/facets/RatingFacet";
import Refinements from "@/app/search/facets/refinements";
import { CategoryFacetType } from "@/app/search/utils/createUsableCategoryFacet";
import { FilterType } from "@/types/search";
import type { AlgoliaQuery } from "@/declarations/algoliaQuery";
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import styles from "./facetBar.module.scss";
import filterstyles from "./filters.module.scss";

type FiltersContentProps = {
  categoryResetSignal: number;
  setCategoryResetSignal: Dispatch<SetStateAction<number>>;
  facets: Record<string, Record<string, number>>;
  categoryFacets: CategoryFacetType[];
  categoryPageName?: string;
  filterType?: FilterType;
  isMobile?: boolean;
  setSelectedValues: Dispatch<SetStateAction<Record<string, Record<string, boolean>>>>;
  selectedValues?: Record<string, Record<string, boolean>>;
  searchQuery: AlgoliaQuery;
};

export default function FiltersContent({
  categoryResetSignal,
  setCategoryResetSignal,
  facets,
  categoryFacets,
  categoryPageName,
  filterType = "default",
  isMobile = false,
  setSelectedValues,
  selectedValues = {},
  searchQuery,
}: FiltersContentProps) {
  const indexName = searchQuery.indexName;
  const { debouncedModifyFacets } = useSearchRouter(indexName);
  const [unselectAllFacetsTrigger, setUnselectAllFacetsTrigger] = useState<number>(0);
  const handleUnselectAll = () => {
    setUnselectAllFacetsTrigger(unselectAllFacetsTrigger + 1);
  };

  const handleFacetValueEvent = useCallback(
    (event: React.SyntheticEvent, facetName: string, value: string) => {
      const checked = (event.target as HTMLInputElement).checked;

      setSelectedValues((prev) => {
        const prevFacetValues = prev[facetName] || {};
        const updatedFacetValues = {
          ...prevFacetValues,
          [value]: checked,
        };

        const updated = {
          ...prev,
          [facetName]: updatedFacetValues,
        };

        debouncedModifyFacets(updated);
        return updated;
      });
    },
    [debouncedModifyFacets, setSelectedValues],
  );

  if (filterType === "brand" && isMobile) {
    {
      return Object.entries(facets).map(([facetName, values]) => {
        if (!values || Object.keys(values).length === 0) return null;
        if (facetName === "brand") {
          const prettifiedName = prettifyFacetName(facetName);

          return (
            <div key={`${facetName}-container`}>
              <GenericFacet
                key={facetName}
                displayFacetName={prettifiedName}
                facetName={facetName}
                startingFacetValues={values}
                handleChange={(event, value) => handleFacetValueEvent(event, facetName, value)}
                selectedValues={selectedValues}
                unselectTrigger={unselectAllFacetsTrigger}
              />
            </div>
          );
        }
      });
    }
  }

  if (filterType === "manufacturer" && isMobile) {
    return Object.entries(facets).map(([facetName, values]) => {
      if (!values || Object.keys(values).length === 0) return null;
      if (facetName === "manufacturer") {
        const prettifiedName = prettifyFacetName(facetName);

        return (
          <div key={`${facetName}-container`}>
            <GenericFacet
              key={facetName}
              displayFacetName={prettifiedName}
              facetName={facetName}
              startingFacetValues={values}
              handleChange={(event, value) => handleFacetValueEvent(event, facetName, value)}
              selectedValues={selectedValues}
              unselectTrigger={unselectAllFacetsTrigger}
            />
          </div>
        );
      }
    });
  }
  if (filterType === "default") {
    return (
      <ul className={styles.listContainer}>
        {!isMobile && (
          <>
            <div className={styles.desktopLocationDetails}>
              <GetDeliveryLocationDetails hideBelowLg />
            </div>
            <div className={filterstyles.filterBySection}>
              <div className={filterstyles.filtersHeading}>Filter by</div>
              <Refinements
                excludedAttributes={["category.lvl0", "query"]}
                setCategoryResetSignal={setCategoryResetSignal}
                setSelectedValues={setSelectedValues}
                unselectAllFacetsClientSideCallback={handleUnselectAll}
              />
              <div className={filterstyles.divider} />
              <CustomFacet
                id="purchasedBefore"
                name="Purchased Before"
                hoverText="Shows products you’ve purchased before."
                requireAuth
                variant="checkbox"
              />
              <div className={filterstyles.divider} />
            </div>
          </>
        )}
        <TopLevelCategoryFacet
          facets={categoryFacets}
          categoryPageName={categoryPageName}
          categoryResetSignal={categoryResetSignal}
          indexName={indexName}
        />
        {Object.entries(facets).map(([facetName, values]) => {
          if (!values || Object.keys(values).length === 0) return null;
          const prettifiedName = prettifyFacetName(facetName);

          if (facetName === STAR_RATING_ATTRIBUTE_NAME) {
            return <RatingFacet key={facetName} values={values} indexName={indexName} />;
          }

          return (
            <GenericFacet
              key={facetName}
              displayFacetName={prettifiedName}
              facetName={facetName}
              startingFacetValues={values}
              handleChange={(event, value) => handleFacetValueEvent(event, facetName, value)}
              selectedValues={selectedValues}
              unselectTrigger={unselectAllFacetsTrigger}
            />
          );
        })}
      </ul>
    );
  }
}

function prettifyFacetName(name: string): string {
  return name.replace(/_/g, " ").replace("attributes.", "");
}
